<template>
  <label
    :class="[
      'q-radio',
      `q-radio--${groupSize}`,
      {
        'q-radio--disabled': isDisabled,
        'q-radio--hover': hover,
        'q-radio--selected': isSelected,
      },
    ]"
    :style="{ opacity: isDisabled ? 0.6 : 1 }"
  >
    <input
      type="radio"
      :name="groupName"
      :value="value"
      :checked="isSelected"
      :disabled="isDisabled"
      class="q-radio__input"
      @change="handleChange"
      @click="$emit('click', $event)"
    />

    <div class="q-radio__circle">
      <div class="q-radio__dot"></div>
    </div>

    <span class="q-radio__text">
      <slot />
    </span>
  </label>
</template>

<script setup>
import { inject, computed } from 'vue';

const props = defineProps({
  modelValue: {
    type: [String, Number, Boolean],
    default: null,
  },
  value: {
    type: [String, Number, Boolean],
    required: true,
  },
  name: {
    type: String,
    default: '',
  },
  size: {
    type: String,
    default: 'l',
    validator: (value) => ['l', 's'].includes(value),
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  hover: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['update:modelValue', 'click']);

// Inject radio group context if available
const radioGroup = inject('radioGroup', null);

// Use group context or local props
const groupName = computed(() => radioGroup?.name || props.name);
const groupSize = computed(() => radioGroup?.size || props.size);
const isDisabled = computed(() => {
  // Если есть radioGroup, используем его disabled состояние
  if (radioGroup) {
    return radioGroup.disabled.value;
  }
  // Иначе используем локальное disabled состояние
  return props.disabled;
});
const isSelected = computed(() => {
  if (radioGroup) {
    return radioGroup.selectedValue.value === props.value;
  }
  return props.modelValue === props.value;
});

const handleChange = (event) => {
  if (isDisabled.value) return;

  if (radioGroup) {
    radioGroup.updateValue(props.value);
  } else {
    emit('update:modelValue', event.target.checked ? props.value : null);
  }
};
</script>

<style lang="scss" scoped>
.q-radio {
  display: flex;
  align-items: center;
  gap: 32px;
  cursor: pointer;
  font-family: 'Inter', sans-serif;
  font-weight: 500;
  transition: all 0.2s ease;
  position: relative;

  // Large size (согласно макету)
  &--l {
    font-size: 32px;
    line-height: normal;

    .q-radio__circle {
      width: 56px;
      height: 56px;
      border: none;
      border-radius: 50%;
      background: rgba(14, 14, 34, 0.1);
      display: flex;
      align-items: center;
      justify-content: center;
      transition: all 0.2s ease;
      box-shadow: none;
      flex-shrink: 0;
      position: relative;
    }

    .q-radio__dot {
      width: 48px;
      height: 48px;
      border-radius: 50%;
      background: transparent;
      transition: all 0.2s ease;
      transform: scale(0);
    }
  }

  // Small size
  &--s {
    font-size: 14px;
    line-height: 1.2;
    gap: 12px;

    .q-radio__circle {
      width: 20px;
      height: 20px;
      border: none;
      border-radius: 50%;
      background: rgba(14, 14, 34, 0.1);
      display: flex;
      align-items: center;
      justify-content: center;
      transition: all 0.2s ease;
      box-shadow: none;
      flex-shrink: 0;
      position: relative;
    }

    .q-radio__dot {
      width: 12px;
      height: 12px;
      border-radius: 50%;
      background: transparent;
      transition: all 0.2s ease;
      transform: scale(0);
    }
  }

  &__input {
    position: absolute;
    opacity: 0;
    width: 0;
    height: 0;
    pointer-events: none;
  }

  &__text {
    color: rgba(14, 14, 34, 1);
    transition: color 0.2s ease;
    user-select: none;
    font-weight: 500;
  }

  // Hover state
  &:hover:not(.q-radio--disabled) {
    .q-radio__circle {
      background: rgba(14, 14, 34, 0.2);
      transform: scale(1.05);
    }

    .q-radio__text {
      color: rgba(14, 14, 34, 0.9);
    }
  }

  // Selected hover state
  &--selected:hover:not(.q-radio--disabled) {
    .q-radio__circle {
      background: #3b82f6;
    }
  }

  // Selected state (согласно макету)
  &--selected {
    .q-radio__circle {
      background: #6ca1f8;
      box-shadow: none;
      position: relative;

      &::after {
        content: '';
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        width: 18px;
        height: 18px;
        border: 1.5px solid var(--color-white);
        border-radius: 50%;
        pointer-events: none;
      }
    }

    .q-radio__dot {
      background: transparent;
      transform: scale(0);
    }

    .q-radio__text {
      color: rgba(14, 14, 34, 1);
      font-weight: 500;
    }
  }

  // Focus state for accessibility
  &__input:focus + .q-radio__circle {
    outline: 2px solid #3b82f6;
    outline-offset: 2px;
  }

  // Disabled state
  &--disabled {
    cursor: not-allowed;

    .q-radio__circle {
      background: rgba(14, 14, 34, 0.1);
      box-shadow: none;
    }

    .q-radio__text {
      color: #6b7280;
    }
  }
}

// Animation for better UX
@keyframes radioPulse {
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.1);
  }
  100% {
    transform: scale(1);
  }
}

.q-radio--selected .q-radio__circle {
  animation: radioPulse 0.3s ease-out;
}

// Fallback styles for better visibility
.q-radio__circle {
  min-width: 16px;
  min-height: 16px;
  border: none !important;
  background: rgba(14, 14, 34, 0.1) !important;
  border-radius: 50% !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
}
</style>
